import { assign } from '@xstate/immer'
import { castDraft, createDraft, current } from 'immer'
import type { WritableDraft } from 'immer/dist/internal'
import { map, Observable } from 'rxjs'
import { createMachine, send } from 'xstate'

import type { Edge, Node } from '../entities'
import {
  DraftNormalGraphLayoutData,
  DraftTreeGraphData,
  EventInfo,
  EVENT_NAME,
} from '../types'
import type {
  GraphStoreContext,
  GraphStoreEvents,
  SetEdgeElementEvent,
  SetNodeElementEvent,
  UpdateEdgePropertiesEvent,
  UpdateNodePropertiesEvent,
  UpdateSizeEvent,
  UpdateTransformEvent,
} from './graph-store-types'

export const createGraphStore = ({
  emit,
  context,
}: {
  emit(eventName: EVENT_NAME, eventInfo?: EventInfo): void
  context: Omit<GraphStoreContext, 'nodes' | 'edges' | 'transform'>
}) => {
  /** @xstate-layout N4IgpgJg5mDOIC5RQE4EMAOALAxAUQFkBJAFQH08A1PAORIG0AGAXUVAwHtYBLAF244A7NiAAeiABwBWCQDoALAE5FE6QEZFAdnmaNAGhABPRPJ2yATADYpl82qmbFOtWoC+rg6ky4AqgAUAEQBBEjwyAGUiAC08JlYkEE4efiERcQRpKVkAZik1eQlGbMU7bPMDYwRzRWycqRrHeQ1LFsV3T3RsHH9g0LIAYQB5GgAxIgBxHwAlWJYRJL4BYQT0iXM5IollRkY7RXtFCsQ1a1lFRlt5O3rCpql2kC8unpCwkimgmnCRwamCOPmXEWqRWknM8lklkY+QK+UYEjUmmyRwQanMDyeWFk3EEizQABscEQaKQiEEADLRWbxdhAlLLUDpcz1M7KZQ6bL2dZqFGaBwWPl2exSByMKTZDGdLEYFBgDBoWU4cJ4cjkoIATUGPnIQzoeAAGgw5gkFvS0sdHIpZIwajZTHlzMzLCjLEoLNkJHz5JZcvJ6pLvLIZXKFWAcP0ABKfcZhXpBAEmulLc0IBwo8zw8yycXZXZWXLadEeR5S2SytAQQz4fV4frasJqzXahO05LJ0EID1qa3Cpw2yyKcUSdMaCGKWyaSzaJFIooB7BlsAVquR6OxkLx42t4EMsSILs9kV98eDj3p8yTs62SzSaz1C-3YuYxfLpUqsiNrU64ahQ0txJJiCjL7gih71PI-ansORiIOCjAQncnrirougSPI85YuWlbdIErwfhqX4DMMYzjP+prtsBnagdCR4QSeQ7plIOw5AOjCaPBnLjuhT6llhVZBAEARkDQgwBNSgJtkBe5Ud2NHgZBDEwVUE6yGs1xSOYuRbBIGEvthMwEIM1DCaJ4mJpJu7pAecnHgOimVBeoGaOspgqHkPq6XxOAGUZYQiWJZAAELqmRgGWSBsm9nRdlnkp1S5JC2Q6KY3pIrYj4dIGXkvH0-lmduZodtZUUKbFlS5ls2YQWsJT1B6zKeUu2ECUJeABDGoUWSmxW0aV0HleCtSmLomlIS4GUlllTVVj5xltR1W4AV1RXUSV9FlfuxSaHUSVqGh5xoZojWvrNYTzWEwWdTu3Wrb1639fuIrdsyg5FOs6xTsd2E5Wd7X5Ut10rZFd0xQ9VTwbJdrOdIlo6TxU2vsq5B5eEFB-eEV2FZRJRyLo8LZLkbHSOCKKIsokJFBBmlik4iJfVWlIjMjpkY4t5FSekThWsoGYqD6+Tgs6SknH6kJaGobGsbzlj0zgjPM2JmMUdJXOsrzEj81c3qk3kciDVoHGU9Yss-SZAV+FMgx+HgUwkEQeCszSANY9JWyWJCBQQT6+xseOpPZK6OTZJo0MXmKWsm7hfTnWQFtWzbdsO0rHOSFosiODecHMk6mik3YjAKJyBSWC4bH1bLSNm2d5KELQRpO+z4UICUQ3B-IxTh9Yrqk2K7saRpiLOdUR3wwuXmVzHeA1wQdfJ03JQQnVU5WI6aKaSifrQqph35gO-MV++eUUPq-Tkj4kTGeEJCvHPKaqNzbcOI6jSaGD7faIXahlDeuZonDmVj2mm+cgk8T5nwvmEK+N82ZhTvvsa00hPTFzsOCQ4Sktbuy0AiYOLh27BwPgrB2ERr6hFvh2GoBdcg1C-kxDk8EN4uCtP3ewQ9HT+lHphIBE90bEOgQ3WB5DOTZjYkiFwHpzjaBRMUEo1pdDjj5Bcb0aFZanzwEEKYVdUZQNCI7CSgNsb5HTkoV6Gk8xoSkRrCEJdtKTnyB3CUHC9IrhruotGMYtEkKTjA5aBisjaQDgiKQpgxRSCkTOWQJwHBYLcsoemshYC8AVLwckaBDAcAAK68BwGQyiaEIRlEKNoNi+QbC5zijeReFw4Tf29DLRxfFZD4lSRkrJEAhBgGxIIAAbhwAA1h0sAogwAAGNMlgBSWkzJOTpJokDqgt2ksCbdziuxOQBMaga1yBeAmE1nwNKaZMrJYAUAoA4CgIMTTeAADMzkAFtZCDJGWMiZLTpnpFmRCeZ45FkB3kOmFoBdl4VJ0K6aQDjiyCA4BAOAIhMR6JdukAAtDyJSNhuxIiQU-cUeQR4AKxDiPE+J4XK3SDoXke1szZzYjcFQ7ddLBnlLKYlKdOwqHTl3EOmkeZKDKZUBRET9iej5DeQonJ6bMqbgTWoNlopQVJucK0vcCh4xULYuJGBjk8ASWAQQvAJUpnyD6VkKguK7HsELBy1gszwhqOycULC4kJKSS8zJ+qOw3iyFQpoPomjnBaP8qE1oLiKJ9P3AOcSDktLddjYoCg7CvySvajM5RylCKdNCQcT1oTcXcEAA */
  return createMachine(
    {
      context: {
        ...(context as GraphStoreContext),
        transform: [0, 0, 1],
        layoutAlgorithm: undefined,
        modelFactory: undefined,
        positions: {},
        nodes: [],
        edges: [],
      },
      tsTypes: {} as import('./graph-store.typegen').Typegen0,
      schema: {
        context: {} as GraphStoreContext,
        events: {} as GraphStoreEvents,
        services: {} as {
          executeLayout: {
            data: {}
          }
        },
      },
      id: 'graph',
      initial: 'initial',
      on: {
        EMIT_EVENT: {
          actions: 'emitEvent',
          description: '触发事件',
        },
        UPDATE_SIZE: {
          actions: [
            'updateSize',
            send<GraphStoreContext, UpdateSizeEvent>((_, { data }) =>
              // 画布大小改变后发送 resize 事件
              data.emitEvent
                ? {
                    type: 'EMIT_EVENT',
                    data: {
                      eventName: EVENT_NAME.resize,
                    },
                  }
                : {
                    type: '',
                  }
            ),
          ],
          description: '更新画布大小',
        },
        UPDATE_CONFIGURE: {
          actions: 'updateConfigure',
          description: '更新配置',
        },
        UPDATE_TRANSFORM: {
          actions: [
            'updateTransform',
            send<GraphStoreContext, UpdateTransformEvent>({
              type: 'EMIT_EVENT',
              data: {
                eventName: EVENT_NAME.viewportchange,
              },
            }),
          ],
          description: '更新画布偏移',
        },
      },
      states: {
        initial: {
          exit: send({
            type: 'EMIT_EVENT',
            data: {
              eventName: EVENT_NAME.afterinit,
            },
          }),
          description: '初始化阶段，等待渲染器就绪',
          on: {
            INITIALIZE: {
              target: 'prepare',
            },
          },
        },
        prepare: {
          description: '准备阶段，等待布局算法上下文及数据就绪',
          on: {
            SET_LAYOUT_CONTEXT: {
              actions: 'setLayoutContext',
            },
            CHANGE_DATA: {
              actions: 'changeData',
              target: 'ready',
            },
          },
        },
        ready: {
          description: '就绪阶段，持续运行',
          initial: 'persistent',
          states: {
            persistent: {
              description: '常态阶段，等待用户动作',
            },
            startLayout: {
              entry: send({
                type: 'EMIT_EVENT',
                data: {
                  eventName: EVENT_NAME.beforelayout,
                },
              }),
              always: {
                target: 'layout',
              },
            },
            layout: {
              exit: send({
                type: 'EMIT_EVENT',
                data: {
                  eventName: EVENT_NAME.afterlayout,
                },
              }),
              description: '布局阶段，等待布局算法完成',
              invoke: {
                src: 'executeLayout',
                id: 'executeLayout',
                onDone: [
                  {
                    target: 'persistent',
                  },
                ],
                onError: [
                  {
                    target: 'persistent',
                  },
                ],
              },
            },
          },
          on: {
            EXECUTE_LAYOUT: {
              description: '开始布局',
              target: '.startLayout',
            },
            CHANGE_DATA: {
              actions: 'changeData',
              description: '改变数据',
              target: '.startLayout',
            },
            SET_LAYOUT_CONTEXT: {
              actions: 'setLayoutContext',
              target: '.startLayout',
              description: '设置布局上下文',
            },
            UPDATE_LAYOUT_CONFIG: {
              actions: 'updateLayoutConfig',
              description: '更新布局配置',
              target: '.startLayout',
            },
            ADD_NODE: {
              actions: ['applyDataPatch', 'emitEvent'],
              description: '添加节点',
              target: '.startLayout',
            },
            REMOVE_NODE: {
              actions: ['applyDataPatch', 'emitEvent'],
              description: '删除节点',
              target: '.startLayout',
            },
            REMOVE_NODE_BY: {
              actions: ['applyDataPatch', 'emitEvent'],
              description: '根据条件删除节点',
              target: '.startLayout',
            },
            UPDATE_NODE: {
              actions: ['applyDataPatch', 'emitEvent'],
              description: '更新节点',
              target: '.startLayout',
            },
            ADD_EDGE: {
              actions: ['applyDataPatch', 'emitEvent'],
              description: '添加边',
              target: '.startLayout',
            },
            REMOVE_EDGE: {
              actions: ['applyDataPatch', 'emitEvent'],
              description: '删除边',
              target: '.startLayout',
            },
            REMOVE_EDGE_BY: {
              actions: ['applyDataPatch', 'emitEvent'],
              description: '根据条件删除节点边',
              target: '.startLayout',
            },
            UPDATE_EDGE: {
              actions: ['applyDataPatch', 'emitEvent'],
              description: '更新边',
              target: '.startLayout',
            },
            SET_NODES_EDGES: {
              actions: ['setNodesEdges', 'emitPositionUpdate'],
              description: '设置节点、边模型',
            },
            LIFT_NODES: {
              actions: ['liftNodes', 'emitPositionUpdate'],
              description: '更新所有节点位置',
            },
            LIFT_NODE: {
              actions: ['liftNode', 'emitPositionUpdate'],
              description: '更新指定节点位置',
            },
            UPDATE_NODE_PROPERTIES: {
              actions: [
                'updateNodeProperties',
                send<GraphStoreContext, UpdateNodePropertiesEvent>(
                  (context, { data }) => ({
                    type: 'EMIT_EVENT',
                    data: {
                      eventName: data.eventName,
                      eventInfo: {
                        item: context.nodes.find((node) => node.id === data.id),
                      },
                    },
                  })
                ),
              ],
              description: '更新节点配置',
            },
            UPDATE_EDGE_PROPERTIES: {
              actions: [
                'updateEdgeProperties',
                send<GraphStoreContext, UpdateEdgePropertiesEvent>(
                  (context, { data }) => ({
                    type: 'EMIT_EVENT',
                    data: {
                      eventName: data.eventName,
                      eventInfo: {
                        item: context.edges.find((node) => node.id === data.id),
                      },
                    },
                  })
                ),
              ],
              description: '更新节点配置',
            },
            SET_NODE_ELEMENT: {
              actions: [
                'setNodeElement',
                send<GraphStoreContext, SetNodeElementEvent>(
                  (context, { data }) => ({
                    type: 'EMIT_EVENT',
                    data: {
                      eventName: data.eventName,
                      eventInfo: {
                        item: context.nodes.find((node) => node.id === data.id),
                      },
                    },
                  })
                ),
              ],
              description: '设置节点元素',
            },
            SET_EDGE_ELEMENT: {
              actions: [
                'setEdgeElement',
                send<GraphStoreContext, SetEdgeElementEvent>(
                  (context, { data }) => ({
                    type: 'EMIT_EVENT',
                    data: {
                      eventName: data.eventName,
                      eventInfo: {
                        item: context.edges.find((node) => node.id === data.id),
                      },
                    },
                  })
                ),
              ],
              description: '设置边元素',
            },
            SET_NODE_EXCLUSIVE_STATE: {
              actions: 'setNodeExclusiveState',
              description: '设置节点独占状态',
            },
            SET_EDGE_EXCLUSIVE_STATE: {
              actions: 'setEdgeExclusiveState',
              description: '设置边独占状态',
            },
            SET_NODES_STATE: {
              actions: 'setNodesState',
              description: '设置节点集合指定状态',
            },
            SET_EDGES_STATE: {
              actions: 'setEdgesState',
              description: '清理边集合指定状态',
            },
            CLEAR_NODES_STATES: {
              actions: 'clearNodesStates',
              description: '清理节点集合指定状态',
            },
            CLEAR_EDGES_STATES: {
              actions: 'clearEdgesStates',
              description: '清理边集合指定状态',
            },
          },
        },
      },
    },
    {
      actions: {
        setLayoutContext: assign((context, { data }) => {
          const { graphType, layoutAlgorithm, modelFactory } = data

          if (graphType) {
            context.graphType = graphType
          }

          context.layoutAlgorithm = layoutAlgorithm
          context.modelFactory = modelFactory
        }),
        changeData: assign((context, { data }) => {
          const { graphData } = data

          context.graphData = graphData
        }),
        liftNodes: assign((context, event) => {
          context.positions = event.data.nodes.reduce<
            Record<string, [number, number]>
          >(
            (acc, { id, x, y }) => ({
              ...acc,
              [id]: [x, y],
            }),
            {}
          )
        }),
        liftNode: assign((context, event) => {
          const { id, x, y, type } = event.data

          if (type === 'SET') {
            context.positions[id] = [x, y]
          } else {
            context.positions[id] = [
              context.positions[id][0] + x,
              context.positions[id][1] + y,
            ]
          }

          // // 触发节点更新
          // context.nodes.find((node) => node.id === id)?.emitPositionUpdate()
          // // 触发相关边的更新
          // context.edges.forEach((edge) => {
          //   if (edge.source === id || edge.target === id) {
          //     edge.emitPositionUpdate()
          //   }
          // })
        }),
        setNodesEdges: assign((context, event) => {
          context.positions = event.data.positions
          context.nodes = castDraft(event.data.nodes).map((nextNode) => {
            const prevNode = context.nodes.find(
              (node) => node.id === nextNode.id
            )

            // 保留之前的状态
            if (prevNode) {
              const { states, element, eventEmitter } = prevNode

              Object.assign(nextNode, { states, element, eventEmitter })
            }

            return nextNode
          })
          context.edges = castDraft(event.data.edges).map((nextEdge) => {
            const prevEdge = context.edges.find(
              (edge) => edge.id === nextEdge.id
            )

            // 保留之前的状态
            if (prevEdge) {
              const { states, element, eventEmitter } = prevEdge

              Object.assign(nextEdge, { states, element, eventEmitter })
            }

            return nextEdge
          })
        }),
        emitPositionUpdate: (context) => {
          // 触发节点更新
          context.nodes.forEach((node) => {
            node.emitPositionUpdate()
          })
          // 触发边更新
          context.edges.forEach((edge) => {
            edge.emitPositionUpdate()
          })
        },
        applyDataPatch: assign((context, event) => {
          event.data.producer(context)
        }),
        updateEdgeProperties: assign((context, event) => {
          const { id, edgeData } = event.data

          if (context.graphType === 'normal') {
            context.edges.find((edge) => {
              if (edge.id === id) {
                Object.assign(edge, edgeData)

                return true
              }

              return false
            })
          }
        }),
        updateNodeProperties: assign((context, event) => {
          const { id, nodeData } = event.data

          if (context.graphType === 'normal') {
            context.nodes.find((node) => {
              if (node.id === id) {
                Object.assign(node, nodeData)

                return true
              }

              return false
            })

            // 需要触发相关边的更新
            context.edges.forEach((edge) => {
              if (edge.source === id || edge.target === id) {
                edge.renderKey += 1
              }
            })
          }
        }),
        updateConfigure: assign((context, event) => {
          Object.assign(context.configure, event.data.configure)
        }),
        updateTransform: assign((context, event) => {
          context.transform = event.data.transform
        }),
        updateSize: assign((context, event) => {
          context.width = event.data.width
          context.height = event.data.height
        }),
        updateLayoutConfig: assign((context, event) => {
          context.layoutConfig = event.data.layoutConfig
        }),
        setNodeElement: assign((context, event) => {
          const { id, element } = event.data

          context.nodes.find((node) => {
            if (node.id === id) {
              node.element = castDraft(element)

              return true
            }

            return false
          })
          // 需要触发相关边的更新
          context.edges.forEach((edge) => {
            if (edge.source === id || edge.target === id) {
              edge.renderKey += 1
            }
          })
        }),
        setEdgeElement: assign((context, event) => {
          const { id, element } = event.data

          context.edges.find((edge) => {
            if (edge.id === id) {
              edge.element = castDraft(element)

              return true
            }

            return false
          })
        }),
        setNodeExclusiveState: assign((context, event) => {
          const { id, state, list = [] } = event.data

          context.nodes.forEach((node) => {
            if (node.id === id || list.includes(node.id)) {
              node.setState(state)
            } else {
              node.setState(state, false)
            }
          })
        }),
        setEdgeExclusiveState: assign((context, event) => {
          const { id, state, list = [] } = event.data

          context.edges.forEach((edge) => {
            if (edge.id === id || list.includes(edge.id)) {
              edge.setState(state)
            } else {
              edge.setState(state, false)
            }
          })
        }),
        setNodesState: assign((context, event) => {
          const { state, enable, ids } = event.data
          const customizer = (node: WritableDraft<Node>) => {
            node.setState(state, enable)
          }

          if (ids) {
            context.nodes
              .filter((node) => ids.includes(node.id))
              .forEach(customizer)
          } else {
            context.nodes.forEach(customizer)
          }
        }),
        setEdgesState: assign((context, event) => {
          const { state, enable, ids } = event.data
          const customizer = (edge: WritableDraft<Edge>) => {
            edge.setState(state, enable)
          }

          if (ids) {
            context.edges
              .filter((edge) => ids.includes(edge.id))
              .forEach(customizer)
          } else {
            context.edges.forEach(customizer)
          }
        }),
        clearNodesStates: assign((context, event) => {
          const { states, ids } = event.data
          const customizer = (node: WritableDraft<Node>) => {
            node.clearStates(states)
          }

          if (ids) {
            context.nodes
              .filter((node) => ids.includes(node.id))
              .forEach(customizer)
          } else {
            context.nodes.forEach(customizer)
          }
        }),
        clearEdgesStates: assign((context, event) => {
          const { states, ids } = event.data
          const customizer = (edge: WritableDraft<Edge>) => {
            edge.clearStates(states)
          }

          if (ids) {
            context.edges
              .filter((edge) => ids.includes(edge.id))
              .forEach(customizer)
          } else {
            context.edges.forEach(customizer)
          }
        }),
        emitEvent: (_, event) => {
          const { eventName, eventInfo } = event.data

          if (eventName) {
            emit(eventName, eventInfo)
          }
        },
      },
      services: {
        executeLayout: ({
          graphData,
          nodes,
          graphType,
          layoutAlgorithm,
          modelFactory,
          layoutConfig,
        }) => {
          if (layoutAlgorithm == null) {
            throw Error('layout algorithm not ready')
          }

          if (graphData == null) {
            throw Error('graph data not ready')
          }

          if (modelFactory == null) {
            throw Error('modelFactory not ready')
          }

          if (graphType === 'normal') {
            const nodeMap = nodes.reduce<Record<Node['id'], Node>>(
              (acc, node) => {
                acc[node.id] = node
                return acc
              },
              {}
            )
            const {
              nodes: nodeModels,
              edges: edgeModels,
              positions,
            } = modelFactory({
              ...graphData,
              nodes: graphData.nodes.map((node) => {
                const { id, x, y } = node

                if (nodeMap[id] && !x && !y) {
                  return {
                    ...node,
                    x: nodeMap[node.id].x,
                    y: nodeMap[node.id].y,
                  }
                }

                return node
              }),
            })

            // 一般图布局是使用初始化好的节点和边进行布局，因为部分布局依赖节点宽度等数据
            const draft: DraftNormalGraphLayoutData = createDraft({
              nodes: nodeModels.map((node) => node.toLayoutData()),
              edges: edgeModels.map((edge) => edge.toLayoutData()),
            })

            return new Observable<GraphStoreEvents>((subscriber) => {
              // 布局前更新 context
              subscriber.next({
                type: 'SET_NODES_EDGES',
                data: {
                  nodes: nodeModels,
                  edges: edgeModels,
                  positions,
                },
              })

              layoutAlgorithm(draft, layoutConfig).subscribe({
                next: ({ nodes }) => {
                  subscriber.next({
                    type: 'LIFT_NODES',
                    data: {
                      nodes: current(nodes),
                    },
                  })
                },
                error: (error) => {
                  subscriber.error(error)
                },
                complete: () => {
                  subscriber.complete()
                },
              })
            }).pipe(map((event) => event))
          } else {
            // 树图布局是使用原始数据反推节点和边，所以直接使用原始数据进行布局
            const draft: DraftTreeGraphData = createDraft(graphData)

            return layoutAlgorithm(draft).pipe(
              map((layoutData) => {
                const { nodes, edges, positions } = modelFactory(layoutData)

                return {
                  type: 'SET_NODES_EDGES',
                  data: {
                    nodes,
                    edges,
                    positions,
                  },
                }
              })
            )
          }
        },
      },
    }
  )
}
